import { DateTime } from 'luxon';

import { Period } from 'dashboard/lib/unixTime';
import { MetricsTransactionVolumeOverTime } from 'dashboard/models/metrics-transaction';
import {
  MetricsActions,
  MetricsFilterParams,
  MetricsTransactionActionType,
} from 'dashboard/redux/actions/metrics-transaction';
import { Iso8601DateString } from 'lib/types';

export interface TransactionMetricsState {
  filters: MetricsFilterParams;
  lastQueryFilters: MetricsFilterParams;
  volumeOverTime: MetricsTransactionVolumeOverTime;
  /**
   * queriedAt is a separate state object because some of our other data are
   * stored as objects, which makes it hard to colocate. We also split out
   * queried at by UI tab.
   */
  queriedAt: {
    volumeOverTime: Iso8601DateString | undefined;
  };
}

const NOW = DateTime.local();
const DEFAULT_CREATED_AT_START = NOW.minus({ days: 14 }).startOf('day');
const DEFAULT_CREATED_AT_END = NOW.endOf('day');

export const initialTransactionMetricsFilterState: MetricsFilterParams = {
  createdAtStart: undefined,
  createdAtEnd: undefined,
  transactionTypeIds: [],
};

export const initialState: TransactionMetricsState = {
  volumeOverTime: {
    data: [],
    domain: [DEFAULT_CREATED_AT_START.toUnixInteger(), DEFAULT_CREATED_AT_END.toUnixInteger()],
    period: Period.Day,
  },
  lastQueryFilters: initialTransactionMetricsFilterState,
  filters: initialTransactionMetricsFilterState,
  queriedAt: {
    volumeOverTime: undefined,
  },
};

// This is a function so we always get the most up-to-date created at times
const getInitialState = (): TransactionMetricsState => {
  const currentTime = DateTime.local();
  return {
    ...initialState,
    filters: {
      ...initialState.filters,
      createdAtStart: currentTime.minus({ days: 14 }).startOf('day').toISO(),
      createdAtEnd: currentTime.endOf('day').toISO(),
    },
  };
};

export default (
  state: TransactionMetricsState = initialState,
  action: MetricsActions,
): TransactionMetricsState => {
  switch (action.type) {
    case MetricsTransactionActionType.SetMetricsTransactionVolumeOverTime: {
      return {
        ...state,
        volumeOverTime: action.payload.transactionVolumeOverTime,
        lastQueryFilters: {
          ...state.filters,
        },
        queriedAt: {
          ...state.queriedAt,
          volumeOverTime: action.payload.queriedAt,
        },
      };
    }

    case MetricsTransactionActionType.SetMetricsFilters: {
      const newFilters = {
        ...state.filters,
        ...action.payload.filters,
      };

      return {
        ...state,
        filters: newFilters,
      };
    }

    case MetricsTransactionActionType.ResetMetricsDateFilter: {
      const initialState = getInitialState();
      return {
        ...state,
        filters: {
          ...state.filters,
          createdAtStart: initialState.filters.createdAtStart,
          createdAtEnd: initialState.filters.createdAtEnd,
        },
      };
    }

    case MetricsTransactionActionType.ClearMetrics: {
      return { ...getInitialState(), filters: state.filters };
    }

    default:
      return state;
  }
};
